import React, { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import history from "../../utils/history";
import { selectPrivileges } from "../../redux/authSlice";
import { canShowLeafItem, canShowTeamGroup } from "./menuPrivileges";

type Props = {
  menu: any;
  leftOffset: number;
  width: number;
  onNavigate?: () => void;
};

const normalizePath = (path: string) => {
  if (!path) return "";
  const cleaned = path.split("?")[0].replace(/\/+$/, "");
  return cleaned || "/";
};

const parseMenuLink = (link: string) => {
  const raw = String(link || "");
  const qIdx = raw.indexOf("?");
  const pathPart = qIdx >= 0 ? raw.slice(0, qIdx) : raw;
  const query = qIdx >= 0 ? raw.slice(qIdx + 1) : "";
  return {
    path: normalizePath(pathPart),
    params: new URLSearchParams(query),
  };
};

const queryParamsMatch = (
  required: URLSearchParams,
  current: URLSearchParams
) => {
  const entries = Array.from(required.entries());
  if (!entries.length) return true;
  return entries.every(([k, v]) => current.get(k) === v);
};

const linkMatches = (
  link: string | undefined,
  pathname: string,
  search: string = "",
  siblingLinks: string[] = []
) => {
  if (!link) return false;
  const { path: target, params: targetParams } = parseMenuLink(link);
  const current = normalizePath(pathname);
  const currentParams = new URLSearchParams(
    String(search || "").replace(/^\?/, "")
  );

  const pathExact = current === target;
  const pathPrefix = current.startsWith(`${target}/`);
  if (!pathExact && !pathPrefix) return false;

  const targetHasQuery = Array.from(targetParams.keys()).length > 0;

  if (targetHasQuery) {
    return pathExact && queryParamsMatch(targetParams, currentParams);
  }

  const siblingQueryWins = siblingLinks.some((other) => {
    if (!other || other === link) return false;
    const { path: oPath, params: oParams } = parseMenuLink(other);
    if (oPath !== target) return false;
    const oEntries = Array.from(oParams.entries());
    if (!oEntries.length) return false;
    return pathExact && queryParamsMatch(oParams, currentParams);
  });
  if (siblingQueryWins) return false;

  if (pathExact) return true;
  if (!pathPrefix) return false;

  const moreSpecificSibling = siblingLinks.some((other) => {
    const o = normalizePath(other);
    if (!o || o === target || o.length <= target.length) return false;
    return current === o || current.startsWith(`${o}/`);
  });
  return !moreSpecificSibling;
};

export default function LeadsSecondaryPanel({
  menu,
  leftOffset,
  width,
  onNavigate,
}: Props) {
  const location = useLocation();
  const pathname = location?.pathname || "";
  const search = location?.search || "";
  const privileges = useSelector(selectPrivileges) || {};

  const groups = useMemo(() => menu?.subMenu || [], [menu]);

  const visibleGroups = useMemo(() => {
    return groups
      .map((group: any, index: number) => {
        if (menu?.key === "Team" && !canShowTeamGroup(group)) {
          return { group, index, leaves: [], panelId: `panel-${index}` };
        }
        const leaves = (group?.subSubMenu || []).filter((leaf: any) =>
          canShowLeafItem(leaf, menu, privileges)
        );
        return { group, index, leaves, panelId: `panel-${index}` };
      })
      .filter((g: any) => {
        if (g.leaves.length) return true;
        if (
          !(g.group?.subSubMenu || []).length &&
          g.group?.link &&
          canShowLeafItem({ key: g.group.key, ...g.group }, menu, privileges)
        ) {
          return true;
        }
        return false;
      });
  }, [groups, menu, privileges]);

  const allLeafLinks = useMemo(() => {
    const links: string[] = [];
    visibleGroups.forEach((g: any) => {
      (g.leaves || []).forEach((leaf: any) => {
        if (leaf?.link) links.push(String(leaf.link));
      });
      if (!g.leaves?.length && g.group?.link) {
        links.push(String(g.group.link));
      }
    });
    return links;
  }, [visibleGroups]);

  const allPanelIds = useMemo(
    () => visibleGroups.map((g: any) => g.panelId),
    [visibleGroups]
  );

  const expandedKey = allPanelIds.join("|");

  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(allPanelIds)
  );

  useEffect(() => {
    setExpanded(new Set(allPanelIds));
  }, [expandedKey]);

  const togglePanel = (panelId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(panelId)) next.delete(panelId);
      else next.add(panelId);
      return next;
    });
  };

  const navItemClass = (active: boolean) =>
    `relative flex w-full items-center rounded-md px-3 py-2.5 text-left text-[13px] transition-colors ${
      active
        ? "bg-[#F9F0F4] font-semibold text-[#841C39]"
        : "font-medium text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#111827]"
    }`;

  return (
    <aside
      aria-label={`${menu?.name || menu?.key || "Module"} submenu`}
      className="fixed top-0 z-[1190] flex h-screen flex-col border-r border-[#E5E7EB] bg-white"
      style={{
        left: leftOffset,
        width,
        transition: "left 0.2s ease, width 0.2s ease",
      }}
    >
      <div className="flex h-[64px] shrink-0 items-center border-b border-[#E5E7EB] px-4">
        <h2 className="truncate text-[13px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
          {menu?.name || menu?.key || "Menu"}
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto px-2.5 py-3 [scrollbar-width:thin]">
        {visibleGroups.map(({ group, index, leaves, panelId }: any) => {
          if (!leaves.length) {
            const active = linkMatches(
              group.link,
              pathname,
              search,
              allLeafLinks
            );
            return (
              <button
                key={`group-link-${group.subMenuPageName || index}`}
                type="button"
                onClick={() => {
                  history.push(group.link);
                  onNavigate?.();
                }}
                className={`${navItemClass(active)} mb-0.5`}
              >
                {active ? (
                  <span
                    aria-hidden
                    className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-[#841C39]"
                  />
                ) : null}
                <span className="truncate pl-1">{group.subMenuPageName}</span>
              </button>
            );
          }

          const isOpen = expanded.has(panelId);
          const groupHasActive = leaves.some((leaf: any) =>
            linkMatches(leaf?.link, pathname, search, allLeafLinks)
          );

          return (
            <div key={`group-${group.subMenuPageName || index}`} className="mb-3">
              <button
                type="button"
                onClick={() => togglePanel(panelId)}
                className={`mb-1 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.1em] transition-colors ${
                  groupHasActive
                    ? "text-[#841C39]"
                    : "text-[#9CA3AF] hover:text-[#6B7280]"
                }`}
              >
                <span className="truncate">{group.subMenuPageName}</span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`shrink-0 transition-transform ${
                    isOpen ? "rotate-180" : ""
                  }`}
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {isOpen ? (
                <ul className="m-0 list-none space-y-0.5">
                  {leaves.map((leaf: any) => {
                    const active = linkMatches(
                      leaf?.link,
                      pathname,
                      search,
                      allLeafLinks
                    );
                    return (
                      <li
                        key={`leaf-${leaf?.key || ""}-${leaf?.link || ""}-${
                          leaf?.name || leaf?.text || ""
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            if (leaf?.link) {
                              history.push(leaf.link);
                              onNavigate?.();
                            }
                          }}
                          className={navItemClass(active)}
                        >
                          {active ? (
                            <span
                              aria-hidden
                              className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-[#841C39]"
                            />
                          ) : null}
                          <span className="truncate pl-1">
                            {leaf?.text || leaf?.name}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
